import { MsalProvider } from '@azure/msal-react';
import CssBaseline from '@mui/material/CssBaseline';
import { CssVarsProvider, ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import './App.css';
import AgentOwnerOrAdminRoute from './components/shared/AgentOwnerOrAdminRoute/AgentOwnerOrAdminRoute';
import PermissionRoute from './components/shared/PermissionRoute/PermissionRoute';
import PrivateRoute from './components/shared/PrivateRoute/PrivateRoute';
import { msalInstance } from './config/msalConfig';
import { AgentRolesProvider } from './contexes/AgentRolesProvider';
import AuthProvider from './contexes/AuthProvider';
import EmbedProvider from './contexes/EmbedProvider';
import DashboardProvider from './contexes/DashboardProvider';
import FeatureGateProvider from './contexes/FeatureGateProvider';
import { SnackBarProvider } from './contexes/SnackBarProvider';
import { SubscriptionProvider } from './contexes/SubscriptionProvider';
import './i18n'; // Initialize i18n
import Agents from './pages/private/Agents';
import EditAgent from './pages/private/agents/EditAgent';
import EditChatAgent from './pages/private/agents/EditChatAgent';
import NewAgent from './pages/private/agents/NewAgent';
import NewCallAgent from './pages/private/agents/NewCallAgent';
import NewChatAgent from './pages/private/agents/NewChatAgent';
import ShowAgent from './pages/private/agents/ShowAgent';
import ShowChatAgent from './pages/private/agents/ShowChatAgent';
import Dashboard from './pages/private/Dashboard';
import HelpPricing from './pages/private/HelpPricing';
import Knowledges from './pages/private/Knowledges';
import EditKnowledge from './pages/private/knowledges/EditKnowledge';
import NewKnowledge from './pages/private/knowledges/NewKnowledge';
import Billing from './pages/private/Billing';
import Pricing from './pages/private/Pricing';
import Settings from './pages/private/Settings';
import Integrations from './pages/private/Integrations';
import UserManagement from './pages/private/UserManagement';
import Usage from './pages/private/Usage';
import AccessDenied from './pages/public/AccessDenied';
import CalendarCallback from './pages/public/CalendarCallback';
import CalendarSuccess from './pages/public/CalendarSuccess';
import Index from './pages/public/Index';
import InviteAccept from './pages/public/InviteAccept';
import MixVoIPEntry from './pages/public/MixVoIPEntry';
import NotFound from './pages/public/NotFound';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import Styleguide from './pages/public/Styleguide';
import TermsOfService from './pages/public/TermsOfService';
import { PERMISSIONS } from './utils/permissions';
import theme from './theme';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch when component mounts if data is fresh
      refetchOnReconnect: false, // Don't refetch when reconnecting if data is fresh
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MsalProvider instance={msalInstance}>
          <AuthProvider>
            <FeatureGateProvider>
              <SubscriptionProvider>
                <SnackBarProvider>
                  <DashboardProvider>
                    <CssVarsProvider>
                      <AgentRolesProvider>
                        <Router>
                          <EmbedProvider>
                            <Routes>
                              {/* Public Routes */}
                              {/* In the website elora.lu, when the user clicks on the "Sign in" button, they are redirected to /?from=elora.lu */}
                              <Route path="/" element={<Index />} />
                              <Route
                                path="/styleguide"
                                element={<Styleguide />}
                              />
                              <Route
                                path="/invite/:token"
                                element={<InviteAccept />}
                              />
                              <Route
                                path="/partner/mixvoip"
                                element={<MixVoIPEntry />}
                              />

                              {/* Private Routes */}
                              <Route
                                path="/dashboard"
                                element={
                                  <PermissionRoute
                                    permission={
                                      PERMISSIONS.VIEW_AGENTS_DASHBOARD
                                    }
                                    element={<Dashboard />}
                                  />
                                }
                              />
                              <Route
                                path="/agents"
                                element={
                                  <PermissionRoute
                                    permission={
                                      PERMISSIONS.VIEW_AGENTS_DASHBOARD
                                    }
                                    element={<Agents />}
                                  />
                                }
                              />
                              <Route
                                path="/agents/:id"
                                element={
                                  <AgentOwnerOrAdminRoute
                                    agentType="call"
                                    element={<ShowAgent />}
                                  />
                                }
                              />
                              <Route
                                path="/agents/new"
                                element={
                                  <PermissionRoute
                                    permission={
                                      PERMISSIONS.VIEW_AGENTS_DASHBOARD
                                    }
                                    element={<NewAgent />}
                                  />
                                }
                              />
                              <Route
                                path="/agents/edit/:id"
                                element={
                                  <AgentOwnerOrAdminRoute
                                    agentType="call"
                                    mode="manage"
                                    element={<EditAgent />}
                                  />
                                }
                              />

                              {/* Call Agent Routes */}
                              <Route
                                path="/agents/call/new"
                                element={
                                  <PermissionRoute
                                    permission={
                                      PERMISSIONS.VIEW_AGENTS_DASHBOARD
                                    }
                                    element={<NewCallAgent />}
                                  />
                                }
                              />
                              <Route
                                path="/agents/call/edit/:id"
                                element={
                                  <AgentOwnerOrAdminRoute
                                    agentType="call"
                                    mode="manage"
                                    element={<EditAgent />}
                                  />
                                }
                              />

                              {/* Chat Agent Routes */}
                              <Route
                                path="/agents/chat/new"
                                element={
                                  <PermissionRoute
                                    permission={
                                      PERMISSIONS.VIEW_AGENTS_DASHBOARD
                                    }
                                    element={<NewChatAgent />}
                                  />
                                }
                              />
                              <Route
                                path="/agents/chat/edit/:id"
                                element={
                                  <AgentOwnerOrAdminRoute
                                    agentType="chat"
                                    mode="manage"
                                    element={<EditChatAgent />}
                                  />
                                }
                              />
                              <Route
                                path="/agents/chat/:id"
                                element={
                                  <AgentOwnerOrAdminRoute
                                    agentType="chat"
                                    element={<ShowChatAgent />}
                                  />
                                }
                              />

                              {/* Knowledge Routes */}
                              <Route
                                path="/knowledges"
                                element={
                                  <PrivateRoute element={<Knowledges />} />
                                }
                              />
                              <Route
                                path="/knowledges/new"
                                element={
                                  <PermissionRoute
                                    permission={PERMISSIONS.MANAGE_KNOWLEDGE}
                                    element={<NewKnowledge />}
                                  />
                                }
                              />
                              <Route
                                path="/knowledges/edit/:id"
                                element={
                                  <PermissionRoute
                                    permission={PERMISSIONS.MANAGE_KNOWLEDGE}
                                    element={<EditKnowledge />}
                                  />
                                }
                              />

                              <Route
                                path="/settings"
                                element={
                                  <PrivateRoute element={<Settings />} />
                                }
                              />

                              <Route
                                path="/user-management"
                                element={
                                  <PermissionRoute
                                    permission={PERMISSIONS.MANAGE_USERS}
                                    element={<UserManagement />}
                                  />
                                }
                              />

                              <Route
                                path="/pricing"
                                element={
                                  <PermissionRoute
                                    permission={PERMISSIONS.MANAGE_BILLING}
                                    element={<Pricing />}
                                  />
                                }
                              />
                              <Route
                                path="/billing"
                                element={
                                  <PermissionRoute
                                    permission={PERMISSIONS.MANAGE_BILLING}
                                    deniedTo="/dashboard"
                                    element={<Billing />}
                                  />
                                }
                              />
                              <Route
                                path="/usage"
                                element={
                                  <PermissionRoute
                                    permission={PERMISSIONS.MANAGE_BILLING}
                                    deniedTo="/dashboard"
                                    element={<Usage />}
                                  />
                                }
                              />

                              <Route
                                path="/help"
                                element={
                                  <PrivateRoute element={<HelpPricing />} />
                                }
                              />

                              <Route
                                path="/integrations"
                                element={
                                  <PrivateRoute element={<Integrations />} />
                                }
                              />

                              {/* Catch-All Route */}
                              <Route
                                path="*"
                                element={<Navigate to="/page-not-found" />}
                              />

                              {/* 404 Page Not Found */}
                              <Route
                                path="/page-not-found"
                                element={<NotFound />}
                              />

                              <Route
                                path="/access-denied"
                                element={<AccessDenied />}
                              />

                              <Route
                                path="/privacy-policy"
                                element={<PrivacyPolicy />}
                              />

                              <Route
                                path="/terms-of-service"
                                element={<TermsOfService />}
                              />

                              {/* Calendar OAuth Callback */}
                              <Route
                                path="/auth/callback"
                                element={<CalendarCallback />}
                              />

                              {/* Calendar OAuth Success */}
                              <Route
                                path="/calendar/success"
                                element={<CalendarSuccess />}
                              />
                            </Routes>
                          </EmbedProvider>
                        </Router>
                      </AgentRolesProvider>
                    </CssVarsProvider>
                  </DashboardProvider>
                </SnackBarProvider>
              </SubscriptionProvider>
            </FeatureGateProvider>
          </AuthProvider>
        </MsalProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
